import {
	Actor,
	type ActorSubclass,
	type HttpAgent,
} from "@icp-sdk/core/agent";
import { IDL } from "@icp-sdk/core/candid";
import { Principal } from "@icp-sdk/core/principal";
import { natToAmount } from "@motokomaker/ic-client";
import { getEnvMap } from "@/lib/network";

export type IndexTokenId = "USDM" | "MMK";

export type WalletTxKind =
	| "transfer_in"
	| "transfer_out"
	| "mint"
	| "burn"
	| "approve"
	| "other";

export type WalletTxRow = {
	id: string;
	token: IndexTokenId;
	kind: WalletTxKind;
	amount: number;
	amountE8: bigint;
	timestampNs: bigint;
	counterparty: string | null;
	/** Ledger from account (principal text) */
	fromAccount: string | null;
	/** Ledger to account (principal text) */
	toAccount: string | null;
	blockIndex: bigint;
};

export function resolveIndexCanisterId(token: IndexTokenId): string | null {
	const env = getEnvMap();
	const raw =
		token === "USDM"
			? env.VITE_CANISTER_ID_USDM_INDEX
			: env.VITE_CANISTER_ID_MMK_INDEX;
	const id = (raw ?? "").trim();
	return id || null;
}

const indexIdlFactory: IDL.InterfaceFactory = ({ IDL: I }) => {
	const Account = I.Record({
		owner: I.Principal,
		subaccount: I.Opt(I.Vec(I.Nat8)),
	});
	const Transfer = I.Record({
		to: Account,
		fee: I.Opt(I.Nat),
		from: Account,
		memo: I.Opt(I.Vec(I.Nat8)),
		created_at_time: I.Opt(I.Nat64),
		amount: I.Nat,
		spender: I.Opt(Account),
	});
	const Mint = I.Record({
		to: Account,
		memo: I.Opt(I.Vec(I.Nat8)),
		created_at_time: I.Opt(I.Nat64),
		amount: I.Nat,
	});
	const Burn = I.Record({
		from: Account,
		memo: I.Opt(I.Vec(I.Nat8)),
		created_at_time: I.Opt(I.Nat64),
		amount: I.Nat,
		spender: I.Opt(Account),
	});
	const Approve = I.Record({
		fee: I.Opt(I.Nat),
		from: Account,
		memo: I.Opt(I.Vec(I.Nat8)),
		created_at_time: I.Opt(I.Nat64),
		amount: I.Nat,
		expected_allowance: I.Opt(I.Nat),
		expires_at: I.Opt(I.Nat64),
		spender: Account,
	});
	const Transaction = I.Record({
		burn: I.Opt(Burn),
		kind: I.Text,
		mint: I.Opt(Mint),
		approve: I.Opt(Approve),
		timestamp: I.Nat64,
		transfer: I.Opt(Transfer),
	});
	const TransactionWithId = I.Record({
		id: I.Nat,
		transaction: Transaction,
	});
	const GetTransactions = I.Record({
		balance: I.Nat,
		transactions: I.Vec(TransactionWithId),
		oldest_tx_id: I.Opt(I.Nat),
	});
	return I.Service({
		get_account_transactions: I.Func(
			[
				I.Record({
					max_results: I.Nat,
					start: I.Opt(I.Nat),
					account: Account,
				}),
			],
			[
				I.Variant({
					Ok: GetTransactions,
					Err: I.Record({ message: I.Text }),
				}),
			],
			["query"],
		),
	});
};

type IndexActor = ActorSubclass<{
	get_account_transactions: (args: {
		max_results: bigint;
		start: [] | [bigint];
		account: { owner: Principal; subaccount: [] | [Uint8Array] };
	}) => Promise<
		| {
				Ok: {
					balance: bigint;
					transactions: Array<{
						id: bigint;
						transaction: {
							kind: string;
							timestamp: bigint;
							transfer: [] | [
								{
									from: { owner: Principal };
									to: { owner: Principal };
									amount: bigint;
								},
							];
							mint: [] | [{ to: { owner: Principal }; amount: bigint }];
							burn: [] | [{ from: { owner: Principal }; amount: bigint }];
							approve: [] | [
								{
									from: { owner: Principal };
									amount: bigint;
									spender?: { owner: Principal };
								},
							];
						};
					}>;
					oldest_tx_id: [] | [bigint];
				};
		  }
		| { Err: { message: string } }
	>;
}>;

function principalEq(a: Principal, b: Principal): boolean {
	return a.toText() === b.toText();
}

export type AccountTxPage = {
	rows: WalletTxRow[];
	oldestTxId: bigint | null;
	hasMore: boolean;
};

export async function fetchAccountTransactions(
	agent: HttpAgent,
	token: IndexTokenId,
	owner: Principal,
	maxResults = 15,
	start?: bigint,
): Promise<AccountTxPage> {
	const indexId = resolveIndexCanisterId(token);
	if (!indexId) {
		return { rows: [], oldestTxId: null, hasMore: false };
	}

	const index = Actor.createActor(indexIdlFactory, {
		agent,
		canisterId: indexId,
	}) as IndexActor;

	const result = await index.get_account_transactions({
		max_results: BigInt(maxResults),
		start: start === undefined ? [] : [start],
		account: { owner, subaccount: [] },
	});

	if ("Err" in result) {
		throw new Error(result.Err.message);
	}

	const rows: WalletTxRow[] = [];
	for (const { id, transaction: tx } of result.Ok.transactions) {
		const ts = tx.timestamp;
		if (tx.transfer.length > 0) {
			const t = tx.transfer[0]!;
			const fromText = t.from.owner.toText();
			const toText = t.to.owner.toText();
			const fromMe = principalEq(t.from.owner, owner);
			const toMe = principalEq(t.to.owner, owner);
			rows.push({
				id: `${token}-${id.toString()}`,
				token,
				kind: fromMe ? "transfer_out" : toMe ? "transfer_in" : "other",
				amount: natToAmount(t.amount),
				amountE8: t.amount,
				timestampNs: ts,
				counterparty: fromMe ? toText : fromText,
				fromAccount: fromText,
				toAccount: toText,
				blockIndex: id,
			});
			continue;
		}
		if (tx.mint.length > 0) {
			const m = tx.mint[0]!;
			const toText = m.to.owner.toText();
			rows.push({
				id: `${token}-${id.toString()}`,
				token,
				kind: "mint",
				amount: natToAmount(m.amount),
				amountE8: m.amount,
				timestampNs: ts,
				counterparty: toText,
				fromAccount: "mint",
				toAccount: toText,
				blockIndex: id,
			});
			continue;
		}
		if (tx.burn.length > 0) {
			const b = tx.burn[0]!;
			const fromText = b.from.owner.toText();
			rows.push({
				id: `${token}-${id.toString()}`,
				token,
				kind: "burn",
				amount: natToAmount(b.amount),
				amountE8: b.amount,
				timestampNs: ts,
				counterparty: fromText,
				fromAccount: fromText,
				toAccount: "burn",
				blockIndex: id,
			});
			continue;
		}
		if (tx.approve.length > 0) {
			const a = tx.approve[0]!;
			const fromText = a.from.owner.toText();
			const spenderText = a.spender?.owner?.toText() ?? null;
			rows.push({
				id: `${token}-${id.toString()}`,
				token,
				kind: "approve",
				amount: natToAmount(a.amount),
				amountE8: a.amount,
				timestampNs: ts,
				counterparty: spenderText,
				fromAccount: fromText,
				toAccount: spenderText,
				blockIndex: id,
			});
		}
	}

	const oldestInPage =
		rows.length > 0 ? rows[rows.length - 1]!.blockIndex : null;

	return {
		rows,
		oldestTxId: oldestInPage,
		hasMore: rows.length >= maxResults,
	};
}
